import React, { useEffect, useState } from 'react';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import TurnstileModal from './TurnstileModal';
import Toast from 'react-native-toast-message';
import { accessService } from '../../../services/central-api/access';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import MyDataTable from '../../../components/MyDataTable';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import DataTableHeader from '../../../components/DataTableHeader';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';

export default function Turnstiles({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'Turnstiles'>>();
    const { user: authenticatedUser } = useAuth();

    const [turnstiles, setTurnstiles] = useState<HikTurnstile[]>([]);
    const [selectedTurnstile, setSelectedTurnstile] = useState<HikTurnstile>();
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState({
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessTurnstiles', filters],
        queryFn: () => accessService.getHikTurnstiles(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setTurnstiles(data.rows);
        setTotalItems(data.count);
    }, [data]);

    return (
        <Container>
            <Header title={translate('turnstiles')} />
            <Card>
                <DataTableHeader
                    title={translate('registeredTurnstiles')}
                    zIndex={2}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                grow: 2,
                                wrap: true
                            },
                            {
                                name: translate('userCount'),
                                selector: row => row.userCount || 0,
                                center: true
                            },
                            {
                                button: true,
                                cell: row => <DropdownButton
                                    actions={[{
                                        content: translate('editInformation'),
                                        onPress: () => {
                                            setSelectedTurnstile(row);
                                            setModalVisible(true);
                                        }
                                    }]}

                                />
                            }
                        ]}
                        data={turnstiles}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            {/* Create/Edit Modal */}
            <TurnstileModal
                onClose={(success) => {
                    setSelectedTurnstile(undefined);
                    setModalVisible(false);
                    refetch();
                    if (success) {
                        Toast.show({
                            type: 'sentinelxSuccess',
                            text1: translate('ActionSuccessfully'),
                        });
                    }
                }}
                isModalVisible={isModalVisible}
                turnstileId={selectedTurnstile?.id}
            />
        </Container>
    );
}
